import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { concatMap, filter, forkJoin, map } from 'rxjs';

import { WrapperActionButton } from '@blaze-runtime/engine-domain';
import { Connection, Workflow } from '@blaze-shared/workflow-domain';

import { hasWrapperRefs } from '../../canvas-elements/element-model/element-props-guards';
import { isElementType } from '../../canvas-elements/element-model/element-type-guards';
import { ElementDefinitionsService } from '../../services/element-definitions.service';
import { ElementStylesService } from '../../services/styles';
import { WorkflowCrudService } from '../../services/workflow-crud.service';
import { ActionButtonConnectionsService } from '../../services/workflow-generation/public-api';
import { updateElementProps } from '../elements/elements.actions';
import { ElementsFacade } from '../elements/elements.facade';
import { RuntimeFacade } from '../runtime/runtime.facade';

import { generationActions } from '.';

const ACTIONS_LAYOUT_TRIGGER_PATH = 'content.options.actionsLayout';

@Injectable({ providedIn: 'root' })
export class GenerationEffects {
  private readonly actions$ = inject(Actions);
  private readonly elementDefinitionsService = inject(ElementDefinitionsService);
  private readonly elementsFacade = inject(ElementsFacade);
  private readonly elementStylesService = inject(ElementStylesService);
  private readonly runtimeFacade = inject(RuntimeFacade);
  private readonly workflowCrudService = inject(WorkflowCrudService);
  private readonly actionButtonConnectionsService = inject(ActionButtonConnectionsService);

  /** updates buttons generated with form actions */
  updateFormActions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateElementProps),
      concatLatestFrom(() => [this.elementsFacade.entities$, this.elementStylesService.sheets$]),
      filter(
        ([{ path, elementId }, entities]) =>
          entities[elementId]?.type === 'formDetails' &&
          (path.startsWith('content.formActions') || path === ACTIONS_LAYOUT_TRIGGER_PATH),
      ),
      map(([{ elementId, path }, _, styleSheets]) => {
        return generationActions.containerActionsRegenerationRequest({
          buttonDefinition: this.elementDefinitionsService.getDefinitionSafe('button'),
          containerId: elementId,
          styleSheets,
          actionsLayoutChanged: path === ACTIONS_LAYOUT_TRIGGER_PATH,
        });
      }),
    ),
  );

  containerActionsRegeneration$ = createEffect(() =>
    this.actions$.pipe(
      ofType(generationActions.containerActionsRegenerationRequest),
      concatLatestFrom(() => [this.elementsFacade.entities$, this.runtimeFacade.connections$]),
      map(([{ containerId }, entities, connections]) => {
        const container = entities[containerId];
        if (!hasWrapperRefs(container)) {
          throw new Error('Wrapper refs missing error');
        }

        const updatedWorkflows: Workflow[] = [];
        const refsConnections = Object.entries(container.props.wrapper.refs)
          .flatMap(([refId, buttonId]) => {
            if (!WrapperActionButton.isRefId(refId)) {
              return;
            }

            const buttonElement = entities[buttonId];
            if (!isElementType(buttonElement, 'button')) {
              console.error('Button element not found');
              return;
            }

            const result =
              this.actionButtonConnectionsService.createOrUpdateActionButtonConnections(
                connections,
                container,
                buttonElement,
              );
            if (result?.workflow) {
              updatedWorkflows.push(result?.workflow);
            }
            return [result?.containerConnection, result?.buttonConnection];
          })
          .filter(Boolean) as Connection[];

        return generationActions.containerActionsRegenerationUpdateWorkflows({
          containerId,
          refsConnections,
          updatedWorkflows,
        });
      }),
    ),
  );

  containerActionsRegenerationUpdateWorkflows$ = createEffect(() =>
    this.actions$.pipe(
      ofType(generationActions.containerActionsRegenerationUpdateWorkflows),
      concatMap(({ updatedWorkflows }) =>
        forkJoin(
          updatedWorkflows.map((workflow) => this.workflowCrudService.updateWorkflow(workflow)),
        ),
      ),
      concatMap((workflows) =>
        workflows.map((workflow) =>
          generationActions.containerActionsRegenerationCompleted({ workflow }),
        ),
      ),
    ),
  );
}
